import React, { useState, useMemo } from "react";
import {
  Plus,
  X,
  Info,
  Package,
  Layers,
  Image,
  DollarSign,
  Pencil,
  Trash2,
} from "lucide-react";
import useGlobalInventory from "../../../services/Inventory/useGlobalInventory";
import toast from "react-hot-toast";
import NoIndex from "../../../components/NoIndex";

import { ChevronDown } from "lucide-react";
import { ToastContainer } from "react-toastify";

const AddGlobalInventoryItemModal = ({
  show,
  onClose,
  selectedCategory,
  selectedSubCategory,
  variants = [],
  masterCategories = [],
}) => {
  const SIZE_OPTIONS = useMemo(
    () => [
      "",
      "Small",
      "Medium",
      "Large",
      "Extra Large",
      "Quarter",
      "Half",
      "Three-Quarter",
      "Full",
      "100g",
      "200g",
      "250g",
      "400g",
      "500g",
      "750g",
      "1kg",
      "2kg",
      "5kg",
      "10kg",
      "100ml",
      "200ml",
      "250ml",
      "500ml",
      "750ml",
      "1L",
      "2L",
      "5L",
      "1 Piece",
      "2 Pieces",
      "3 Pieces",
      "Pack of 6",
      "Pack of 12",
      "Single",
      "Double",
      "Family Pack",
      "Combo",
    ],
    [],
  );

  const VARIANT_CATEGORY_GROUPS = useMemo(
    () => [
      {
        label: "Industry",
        options: [
          "Food",
          "Bakery",
          "Grocery",
          "Dairy",
          "Beverages",
          "Household",
          "Personal Care",
          "Pharmacy",
          "Pet Care",
          "Baby Care",
          "Stationery",
          "Home & Kitchen",
          "Cleaning",
          "Electronics",
          "Fashion",
          "Health & Wellness",
        ],
      },
      {
        label: "Food Types",
        options: [
          "Fruits & Vegetables",
          "Staples & Grains",
          "Rice & Flour",
          "Pulses & Lentils",
          "Oils & Ghee",
          "Masala & Spices",
          "Snacks & Namkeen",
          "Biscuits & Cookies",
          "Chocolates & Sweets",
          "Breakfast & Cereals",
          "Instant & Ready-to-Eat",
          "Bakery & Breads",
          "Cakes & Pastries",
          "Dairy & Cheese",
          "Paneer & Tofu",
          "Eggs & Meat",
          "Seafood",
          "Frozen Foods",
          "Beverages - Soft Drinks",
          "Beverages - Juices",
          "Tea & Coffee",
          "Water & Ice",
        ],
      },
    ],
    [],
  );

  const [form, setForm] = useState({
    productName: "",
    weight: "",
    barcode: "",
    variant: "",
    imageUrl: "",
    price: "",
  });
  const [editingVariantId, setEditingVariantId] = useState(null);

const [showWeightDropdown, setShowWeightDropdown] = useState(false);
const [weightSearch, setWeightSearch] = useState("");

const [showVariantDropdown, setShowVariantDropdown] = useState(false);
const [variantSearch, setVariantSearch] = useState("");

  const {
    updateInventoryVariant,
    fetchAllInventoryVariants,
    insertInventoryVariant,
    deleteInventoryVariant,
    insertGlobalInventoryItems,
  } = useGlobalInventory();

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const [editVariant, setEditVariant] = useState({
    title: "",
    category: "",
  });

  const [newVariant, setNewVariant] = useState({
    variantTitle: "",
    category: "",
  });

  const [showVariantManager, setShowVariantManager] = useState(false);
  if (!show) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.productName || !form.price) {
      toast.error("Product name and price required");
      return;
    }

    if (!form.imageUrl) {
      toast.error("Product image required");
      return;
    }

    const selectedVariant = variants.find(
      (v) => v.pk_variant_id === form.variant,
    );

    const payload = {
      items: [
        {
          productName: form.productName,
          category: selectedCategory,
          subCategory: selectedSubCategory,
          barcodeNumber: form.barcode,
          weight: form.weight,
          price: form.price,
          imageUrl: form.imageUrl,
          variantId: form.variant || "",
          variantTitle: selectedVariant?.variant_title || "",
        },
      ],
    };

    const res = await insertGlobalInventoryItems(payload);

    if (res?.responseCode === "00") {
      toast.success("Item added successfully");

      setForm({
        productName: "",
        weight: "",
        barcode: "",
        variant: "",
        imageUrl: "",
        price: "",
      });

      onClose();
    }
  };

  const handleCreateVariant = async () => {
    if (!newVariant.variantTitle || !newVariant.category) {
      toast.error("Variant title and category required");
      return;
    }

    const payload = {
      variantTitle: newVariant.variantTitle,
      category: newVariant.category,
    };

    const res = await insertInventoryVariant(payload);

    if (res?.responseCode === "00") {
      setNewVariant({
        variantTitle: "",
        category: "",
      });

      await fetchAllInventoryVariants(); // refresh list
    }
  };

  const handleVariantUpdate = async () => {
    try {
      const payload = {
        variantId: editingVariantId,
        variantTitle: editVariant.title,
        category: editVariant.category,
      };

      const res = await updateInventoryVariant(payload);

      if (res?.responseCode === "00") {
        setEditingVariantId(null);
        await fetchAllInventoryVariants();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    const res = await deleteInventoryVariant(variantId);

    if (res?.responseCode === "00") {
      toast.success("Variant deleted");

      await fetchAllInventoryVariants();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const base64 = await fileToBase64(file);

    setForm((prev) => ({
      ...prev,
      imageUrl: base64,
    }));
  };

  const filteredWeights = SIZE_OPTIONS.filter((size) =>
  (size || "").toLowerCase().includes(weightSearch.toLowerCase())
);

const filteredVariants = variants.filter((variant) =>
  (variant.variant_title || "")
    .toLowerCase()
    .includes(variantSearch.toLowerCase())
);

  return (
    <>
      <NoIndex />
<ToastContainer/>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 montserrat-font-1">
        <div className="bg-white rounded-2xl shadow-xl w-[750px] max-w-[95%] max-h-[90vh] flex flex-col montserrat-font-1">
          {/* HEADER */}
          <div className="flex justify-between items-center px-6 py-4 border-b montserrat-font-1">
            <div className="flex items-center gap-3 font-semibold text-gray-700">
              <div className="bg-green-100 p-2 rounded-lg ">
                <Plus size={18} className="text-green-600" />
              </div>
              Add Global Inventory Item
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 border"
            >
              <X size={18} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-6 overflow-y-auto flex-1 montserrat-font-1"
          >
            {/* BASIC DETAILS */}
            <div className="border rounded-xl p-5 bg-gray-50 montserrat-font-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <Info size={16} />
                Basic Details
                <span className="text-xs text-gray-400">
                  Core item information
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 montserrat-font-1">
                {/* PRODUCT NAME */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Product Name *
                  </label>

                  <input
                    type="text"
                    name="productName"
                    value={form.productName}
                    onChange={handleChange}
                    placeholder="e.g., Rajdhani Moong Dal"
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* WEIGHT */}
               <div className="relative">
  <label className="text-xs font-medium text-gray-600">
    Weight / Pack Size
  </label>

  <div
    onClick={() => setShowWeightDropdown((prev) => !prev)}
    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-white flex justify-between items-center cursor-pointer"
  >
    <span>{form.weight || "Select"}</span>

  <ChevronDown
  size={18}
  className={`text-gray-600 transition-transform duration-200 ${
    showWeightDropdown ? "rotate-180" : ""
  }`}
/>
  </div>

  {showWeightDropdown && (
    <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg">
      <input
        type="text"
        placeholder="Search..."
        value={weightSearch}
        onChange={(e) => setWeightSearch(e.target.value)}
        className="w-full px-3 py-2 border-b outline-none text-sm"
      />

      <div className="max-h-48 overflow-y-auto">
        {filteredWeights.length ? (
          filteredWeights.map((size, index) => (
            <div
              key={index}
              onClick={() => {
                handleChange({
                  target: {
                    name: "weight",
                    value: size,
                  },
                });

                setShowWeightDropdown(false);
                setWeightSearch("");
              }}
              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm"
            >
              {size}
            </div>
          ))
        ) : (
          <div className="px-3 py-2 text-gray-400 text-sm">
            No weight found
          </div>
        )}
      </div>
    </div>
  )}
</div>

                {/* BARCODE */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Barcode
                  </label>

                  <input
                    type="text"
                    name="barcode"
                    value={form.barcode}
                    onChange={handleChange}
                    placeholder="Scan or type barcode"
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* VARIANT */}
                <div className="flex gap-2">
                  <div className="flex-1 relative">
  <label className="text-xs font-medium text-gray-600">
    Variant
  </label>

  <div
    onClick={() => setShowVariantDropdown((prev) => !prev)}
    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-white flex justify-between items-center cursor-pointer"
  >
    <span>
      {form.variant
        ? variants.find(
            (v) =>
              String(v.pk_variant_id) === String(form.variant)
          )?.variant_title
        : "Select"}
    </span>

    <ChevronDown
      size={18}
      className={`text-gray-600 transition-transform duration-200 ${
        showVariantDropdown ? "rotate-180" : ""
      }`}
    />
  </div>

  {showVariantDropdown && (
    <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg">
      <input
        type="text"
        placeholder="Search variant..."
        value={variantSearch}
        onChange={(e) => setVariantSearch(e.target.value)}
        className="w-full px-3 py-2 border-b outline-none text-sm"
      />

      <div className="max-h-48 overflow-y-auto">
        {filteredVariants.length > 0 ? (
          filteredVariants.map((variant) => (
            <div
              key={variant.pk_variant_id}
              onClick={() => {
                handleChange({
                  target: {
                    name: "variant",
                    value: variant.pk_variant_id,
                  },
                });

                setShowVariantDropdown(false);
                setVariantSearch("");
              }}
              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm"
            >
              {variant.variant_title}
            </div>
          ))
        ) : (
          <div className="px-3 py-2 text-gray-400 text-sm">
            No variants found
          </div>
        )}
      </div>
    </div>
  )}
</div>

                  <button
                    type="button"
                    onClick={() => setShowVariantManager((prev) => !prev)}
                    className="mt-6 border rounded-lg px-3 text-sm hover:bg-gray-100"
                  >
                    + Manage
                  </button>
                </div>
              </div>
            </div>
            {showVariantManager && (
              <div className="border rounded-xl p-5 bg-gray-50 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Layers size={16} />
                    Variant Manager
                    <span className="text-xs text-gray-400">
                      Create / Update / Delete
                    </span>
                  </div>

                  <button
                    onClick={() => setShowVariantManager(false)}
                    className="text-xs text-gray-500 hover:text-black"
                  >
                    Hide
                  </button>
                </div>

                {/* CREATE VARIANT */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {/* Variant Title */}
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-600 mb-1">
                      Variant Title
                    </label>
                    <input
                      type="text"
                      value={newVariant.variantTitle}
                      onChange={(e) =>
                        setNewVariant((prev) => ({
                          ...prev,
                          variantTitle: e.target.value,
                        }))
                      }
                      placeholder="e.g., Organic / Sugar Free"
                      className="border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  {/* Category */}

                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-600 mb-1">
                      Category (for this variant)
                    </label>

                    <select
                      value={newVariant.category}
                      onChange={(e) =>
                        setNewVariant((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Select</option>

                      {VARIANT_CATEGORY_GROUPS.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* Button */}
                  <div className="flex flex-col justify-end">
                    <label className="text-xs font-medium text-gray-600 mb-1 opacity-0">
                      Button
                    </label>
                    <button
                      type="button"
                      onClick={handleCreateVariant}
                      className="bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm px-3 py-2"
                    >
                      + Create
                    </button>
                  </div>
                </div>

                {editingVariantId && (
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {/* TITLE */}
                    <input
                      type="text"
                      value={editVariant.title}
                      onChange={(e) =>
                        setEditVariant((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      className="border rounded-lg px-3 py-2 text-sm"
                    />

                    {/* CATEGORY DROPDOWN */}
                    <select
                      value={editVariant.category}
                      onChange={(e) =>
                        setEditVariant((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Select</option>

                      {VARIANT_CATEGORY_GROUPS.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>

                    {/* UPDATE BUTTON */}
                    <button
                      onClick={handleVariantUpdate}
                      className="bg-blue-600 text-white rounded-lg text-sm px-3"
                    >
                      Update
                    </button>

                    {/* CANCEL */}
                    <button
                      onClick={() => setEditingVariantId(null)}
                      className="border rounded-lg text-sm px-3"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* VARIANT LIST */}

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {variants.map((variant) => (
                    <div
                      key={variant.pk_variant_id}
                      className="flex justify-between items-center border rounded-lg px-3 py-2 bg-white"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {variant.variant_title}
                        </span>

                        {variant.category && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                            {variant.category}
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          className="border rounded-md p-1 hover:bg-gray-100"
                          onClick={() => {
                            setEditingVariantId(variant.pk_variant_id);
                            setEditVariant({
                              title: variant.variant_title,
                              category: variant.category,
                            });
                          }}
                        >
                          <Pencil size={16} className="text-gray-600" />
                        </button>

                        <button
                          onClick={() =>
                            handleDeleteVariant(variant.pk_variant_id)
                          }
                          className="border rounded-md p-1 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* CATEGORY */}
            <div className="border rounded-xl p-5 bg-gray-50">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <Package size={16} />
                Category
                <span className="text-xs text-gray-400">
                  Prefilled from selection
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Category *
                  </label>

                  <input
                    type="text"
                    value={selectedCategory}
                    disabled
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-gray-100"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Sub Category
                  </label>

                  <input
                    type="text"
                    value={selectedSubCategory}
                    disabled
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* MEDIA */}
            <div className="border rounded-xl p-5 bg-gray-50">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <Image size={16} />
                Media
                <span className="text-xs text-gray-400">URL or upload</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Image URL
                  </label>

                  <input
                    type="text"
                    name="imageUrl"
                    value={form.imageUrl}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Or Upload Image
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  />

                  {form.imageUrl && (
                    <img
                      src={form.imageUrl}
                      className="w-20 h-20 object-cover rounded mt-2"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* PRICING */}
            <div className="border rounded-xl p-5 bg-gray-50">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <DollarSign size={16} />
                Pricing
                <span className="text-xs text-gray-400">MRP / Selling</span>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600">
                  Price *
                </label>

                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="e.g., 99.00"
                  className="w-[300px] mt-1 border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
              >
                <X size={16} />
                Cancel
              </button>

              <button
                type="submit"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
              >
                Add Item
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddGlobalInventoryItemModal;
